import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useLinkedHouseholds } from '../linkedHouseholds/hooks/useLinkedHouseholds'
import { useCustodySchedules } from './hooks/useCustody'
import { CustodyCalendar } from './components/CustodyCalendar'
import { UpcomingTransitions } from './components/UpcomingTransitions'
import { ScheduleList } from './components/ScheduleList'
import { ExpensesTab } from './components/ExpensesTab'
import { SkeletonPage } from '../../components/ui/Skeleton'
import { cn } from '../../lib/utils'

// ── Empty states ───────────────────────────────────────────────
const NoLinkedHousehold = () => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="mb-5">
      <rect x="10" y="20" width="28" height="40" rx="6" fill="#f0eaff"/>
      <rect x="42" y="20" width="28" height="40" rx="6" fill="#e8e4f0"/>
      <circle cx="40" cy="40" r="8" fill="#2d1b5e" opacity="0.15"/>
      <path d="M36 40h8M40 36v8" stroke="#2d1b5e" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
    </svg>
    <p className="text-sm font-semibold text-text-primary mb-2">
      Custody tracking works across linked households
    </p>
    <p className="text-xs text-text-muted max-w-xs leading-relaxed mb-6">
      Link your household with your co-parent's household to share custody calendars
      and track shared expenses together.
    </p>
    <Link
      to="/settings/household"
      className="bg-primary text-white rounded-xl px-5 py-2.5 text-sm font-semibold"
    >
      Link a Household
    </Link>
  </div>
)

const NoSchedules = ({ onAdd }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="mb-5">
      <rect x="10" y="16" width="60" height="54" rx="8" fill="#f0eaff"/>
      <rect x="10" y="16" width="60" height="16" rx="8" fill="#e8e4f0"/>
      <rect x="10" y="24" width="60" height="8" fill="#e8e4f0"/>
      <rect x="18" y="40" width="20" height="8" rx="3" fill="#2d1b5e" opacity="0.2"/>
      <rect x="42" y="40" width="20" height="8" rx="3" fill="#7c6faf" opacity="0.3"/>
      <rect x="18" y="54" width="44" height="6" rx="3" fill="#e8e4f0"/>
    </svg>
    <p className="text-sm font-semibold text-text-primary mb-2">No custody schedules yet</p>
    <p className="text-xs text-text-muted max-w-xs leading-relaxed mb-6">
      Set up your first schedule to visualize custody days on a shared calendar.
    </p>
    {onAdd && (
      <button
        type="button"
        onClick={onAdd}
        className="bg-primary text-white rounded-xl px-5 py-2.5 text-sm font-semibold"
      >
        Set Up First Schedule
      </button>
    )}
  </div>
)

const TABS = ['Calendar', 'Schedules', 'Expenses']

export const CustodyPage = () => {
  const { isAdmin, isLimited }      = useAuth()
  const { activeLink, isLoading: linkLoading } = useLinkedHouseholds()
  const { data: schedules = [], isLoading: schedLoading } = useCustodySchedules()

  const [activeTab,    setActiveTab]    = useState('Calendar')
  const [filterChild,  setFilterChild]  = useState(null)

  const sharedChildren = useMemo(() => {
    const seen = new Map()
    schedules.forEach((s) => {
      if (s.child && !seen.has(s.child_id)) seen.set(s.child_id, s.child)
    })
    return Array.from(seen.values())
  }, [schedules])

  if (linkLoading || schedLoading) return <SkeletonPage />

  if (!activeLink) return (
    <div className="max-w-2xl mx-auto">
      <div className="px-4 py-5">
        <h1 className="text-xl font-bold text-text-primary mb-1">Custody</h1>
      </div>
      <NoLinkedHousehold />
    </div>
  )

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-text-primary mb-4">Custody</h1>

      {/* Tab nav */}
      <div className="flex gap-1 mb-5 bg-background rounded-xl p-1">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={cn(
              'flex-1 py-2 rounded-lg text-sm font-semibold transition-colors',
              activeTab === tab
                ? 'bg-surface text-primary shadow-sm'
                : 'text-text-muted',
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Calendar tab */}
      {activeTab === 'Calendar' && (
        <>
          {schedules.length === 0 ? (
            <NoSchedules onAdd={isAdmin ? () => setActiveTab('Schedules') : null} />
          ) : (
            <>
              {/* Child filter chips */}
              {sharedChildren.length > 1 && (
                <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                  <button
                    type="button"
                    onClick={() => setFilterChild(null)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-medium flex-shrink-0 border transition-colors',
                      !filterChild ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary',
                    )}
                  >
                    All children
                  </button>
                  {sharedChildren.map((child) => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => setFilterChild(filterChild === child.id ? null : child.id)}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-sm font-medium flex-shrink-0 border transition-colors',
                        filterChild === child.id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary',
                      )}
                    >
                      {child.display_name}
                    </button>
                  ))}
                </div>
              )}

              <UpcomingTransitions schedules={schedules} />
              <CustodyCalendar schedules={schedules} filterChildId={filterChild} />
            </>
          )}
        </>
      )}

      {/* Schedules tab */}
      {activeTab === 'Schedules' && <ScheduleList />}

      {/* Expenses tab */}
      {activeTab === 'Expenses' && <ExpensesTab />}
    </div>
  )
}
